import React, { useState, useEffect, useMemo } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { useT } from "../../contexts/TranslationContext";
import { displayText } from "../../utils/translationDisplay";
import { resizeImageFiles, ALLOWED_PHOTO_TYPES } from "../../utils/imageResize";
import {
  FLAG_TYPES,
  FLAG_SEVERITIES,
  createDraft,
  taskEntryKey,
  isDraftValid,
} from "./reportFlagDrafts";

const TITLE_KEYS = {
  [FLAG_TYPES.DEVIATION]: "reportFlags.newDeviation",
  [FLAG_TYPES.ATA]: "reportFlags.newAta",
  [FLAG_TYPES.MISSING_TASK]: "reportFlags.newMissingTask",
};

const PLACEHOLDER_KEYS = {
  [FLAG_TYPES.DEVIATION]: "reportFlags.descriptionPlaceholderDeviation",
  [FLAG_TYPES.ATA]: "reportFlags.descriptionPlaceholderAta",
  [FLAG_TYPES.MISSING_TASK]: "reportFlags.descriptionPlaceholderMissingTask",
};

const SEVERITY_KEYS = {
  low: "reportFlags.severityLow",
  medium: "reportFlags.severityMedium",
  blocking: "reportFlags.severityBlocking",
};

const DEVIATION_EXAMPLE_KEYS = [
  "reportFlags.deviationExample.ground",
  "reportFlags.deviationExample.drawings",
  "reportFlags.deviationExample.directives",
  "reportFlags.deviationExample.material",
  "reportFlags.deviationExample.equipment",
  "reportFlags.deviationExample.weather",
  "reportFlags.deviationExample.measurements",
  "reportFlags.deviationExample.scope",
  "reportFlags.deviationExample.utilities",
];

/**
 * Bottom sheet for raising a deviation, ÄTA or missing task.
 *
 * Deviations and ÄTA are description-first: everything that is not needed to act on the
 * report sits behind "Add details", so the common case is one sentence and a tap. A
 * missing task has few enough fields to show them all at once.
 */
function ReportFlagSheet({
  isOpen,
  type,
  drafts = [],
  taskEntries = [],
  initialDraft = null,
  onSave,
  onRemove,
  onClose,
}) {
  const { t } = useT();
  const [editing, setEditing] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeviationHelp, setShowDeviationHelp] = useState(false);
  const [error, setError] = useState("");
  const [processingPhotos, setProcessingPhotos] = useState(false);

  const draftsOfType = useMemo(
    () => drafts.filter((draft) => draft.type === type),
    [drafts, type]
  );

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setShowDetails(false);
    setShowDeviationHelp(false);
    if (initialDraft) {
      setEditing(initialDraft);
    } else if (draftsOfType.length === 0) {
      setEditing(createDraft(type));
    } else {
      setEditing(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, type, initialDraft]);

  if (!isOpen) return null;

  const isMissingTask = type === FLAG_TYPES.MISSING_TASK;

  const update = (patch) => setEditing((prev) => ({ ...prev, ...patch }));

  const handlePhotoChange = async (e) => {
    const files = Array.from(e.target.files || []).filter((file) =>
      ALLOWED_PHOTO_TYPES.includes(file.type)
    );
    if (files.length === 0) return;
    setProcessingPhotos(true);
    const resized = await resizeImageFiles(files, (err) =>
      console.error("Flag photo resize failed:", err)
    );
    setProcessingPhotos(false);
    update({ photos: [...(editing?.photos || []), ...resized] });
  };

  const handleSave = () => {
    if (!isDraftValid(editing)) {
      setError(
        isMissingTask
          ? t("reportFlags.taskNameRequired")
          : t("reportFlags.descriptionRequired")
      );
      return;
    }
    onSave(editing);
    onClose();
  };

  const handleRemove = (localId) => {
    onRemove(localId);
    const remaining = draftsOfType.filter((draft) => draft.localId !== localId);
    if (remaining.length === 0) onClose();
    else setEditing(null);
  };

  const renderList = () => (
    <div className="p-4 space-y-2">
      {draftsOfType.map((draft) => (
        <div
          key={draft.localId}
          className="flex items-start gap-2 p-3 border border-gray-200 rounded-lg"
        >
          <button
            type="button"
            onClick={() => {
              setEditing(draft);
              setShowDetails(false);
            }}
            className="flex-1 text-left min-w-0"
          >
            <div className="text-sm font-medium text-gray-900 truncate">
              {isMissingTask ? draft.proposedTaskName : draft.description}
            </div>
            {isMissingTask && draft.hours && (
              <div className="text-xs text-gray-500">
                {draft.hours}
                {t("timeReports.hourSuffix")}
              </div>
            )}
          </button>
          <button
            type="button"
            onClick={() => handleRemove(draft.localId)}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
            aria-label={t("reportFlags.remove")}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => {
          setEditing(createDraft(type));
          setShowDetails(false);
        }}
        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 text-sm"
      >
        {t("reportFlags.addAnother")}
      </button>
    </div>
  );

  const renderPhotoField = () => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {t("reportFlags.photo")}
      </label>
      <input
        type="file"
        multiple
        accept={ALLOWED_PHOTO_TYPES.join(",")}
        onChange={handlePhotoChange}
        className="block w-full text-sm"
      />
      {processingPhotos && (
        <p className="text-xs text-gray-500 mt-1">{t("report.processingPhotos")}</p>
      )}
      {editing.photos?.length > 0 && (
        <p className="text-xs text-gray-600 mt-1">
          {t("reportFlags.photosAttached", { count: editing.photos.length })}
        </p>
      )}
    </div>
  );

  // A missing task has so few fields that hiding any of them behind a disclosure costs
  // more taps than it saves scrolling, so this form is flat.
  const renderMissingTaskForm = () => (
    <div className="p-4 space-y-4">
      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3">
        <div className="flex-1 min-w-0">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("reportFlags.taskNameLabel")}
          </label>
          <input
            type="text"
            value={editing.proposedTaskName}
            onChange={(e) => update({ proposedTaskName: e.target.value })}
            placeholder={t("reportFlags.taskNamePlaceholder")}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>
        <div className="w-24 flex-shrink-0">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("reportFlags.hoursLabel")}
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={editing.hours}
            onChange={(e) => update({ hours: e.target.value })}
            placeholder="0"
            className="w-full px-3 py-2 border rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("reportFlags.optionalDescription")}
        </label>
        <textarea
          value={editing.description}
          onChange={(e) => update({ description: e.target.value })}
          placeholder={t(PLACEHOLDER_KEYS[type])}
          rows={3}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
      </div>
    </div>
  );

  const renderForm = () => (
    <div className="p-4 space-y-4">
      {error && <p className="text-sm text-red-600">{error}</p>}

      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {t("reportFlags.descriptionLabel")}
          </label>
          {type === FLAG_TYPES.DEVIATION && (
            <button
              type="button"
              onClick={() => setShowDeviationHelp((open) => !open)}
              className={`p-1 rounded-full transition-colors ${
                showDeviationHelp
                  ? "text-amber-600 bg-amber-50"
                  : "text-gray-400 hover:text-amber-600 hover:bg-amber-50"
              }`}
              aria-expanded={showDeviationHelp}
              aria-label={t("reportFlags.deviationHelpToggle")}
            >
              <FaInfoCircle className="w-4 h-4" aria-hidden />
            </button>
          )}
        </div>
        {type === FLAG_TYPES.DEVIATION && showDeviationHelp && (
          <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-100 text-sm text-gray-700">
            <p>{t("reportFlags.deviationHelpIntro")}</p>
            <p className="mt-2 font-medium text-gray-800">
              {t("reportFlags.deviationHelpExamples")}
            </p>
            <ul className="mt-1 list-disc pl-5 space-y-0.5">
              {DEVIATION_EXAMPLE_KEYS.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
          </div>
        )}
        <textarea
          value={editing.description}
          onChange={(e) => update({ description: e.target.value })}
          placeholder={t(PLACEHOLDER_KEYS[type])}
          rows={4}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          autoFocus
        />
      </div>

      {type === FLAG_TYPES.DEVIATION && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("reportFlags.severity")}
          </label>
          <div className="flex gap-2">
            {FLAG_SEVERITIES.map((severity) => (
              <button
                key={severity}
                type="button"
                onClick={() => update({ severity })}
                className={`flex-1 py-2 px-2 rounded-lg border text-sm transition-colors ${
                  editing.severity === severity
                    ? "bg-blue-600 border-blue-600 text-white font-medium"
                    : "bg-white border-gray-300 text-gray-600 hover:border-blue-400"
                }`}
              >
                {t(SEVERITY_KEYS[severity])}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowDetails((s) => !s)}
        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
      >
        <svg
          className={`w-4 h-4 transition-transform ${showDetails ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        {showDetails ? t("reportFlags.hideDetails") : t("reportFlags.addDetails")}
      </button>

      {showDetails && (
        <div className="space-y-4 pt-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("reportFlags.hoursImpact")}
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={editing.hoursImpact}
              onChange={(e) => update({ hoursImpact: e.target.value })}
              placeholder="0"
              className="w-24 px-3 py-2 border rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {taskEntries.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("reportFlags.whichTask")}
              </label>
              <select
                value={editing.linkedTaskKey}
                onChange={(e) => update({ linkedTaskKey: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t("reportFlags.wholeDay")}</option>
                {taskEntries
                  .filter((entry) => entry.taskId)
                  .map((entry) => (
                    <option key={taskEntryKey(entry)} value={taskEntryKey(entry)}>
                      {displayText(entry, "title") || entry.title}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {renderPhotoField()}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      <div className="relative bg-white w-full max-w-lg max-h-[85vh] rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{t(TITLE_KEYS[type])}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2 text-gray-500 hover:text-gray-700"
            aria-label={t("reportFlags.cancel")}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {editing ? (isMissingTask ? renderMissingTaskForm() : renderForm()) : renderList()}
        </div>

        {editing && (
          <div className="p-4 border-t flex items-center gap-2">
            {draftsOfType.some((draft) => draft.localId === editing.localId) && (
              <button
                type="button"
                onClick={() => handleRemove(editing.localId)}
                className="px-3 py-2 text-sm text-red-600 hover:text-red-700"
              >
                {t("reportFlags.remove")}
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={processingPhotos}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {t("reportFlags.save")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReportFlagSheet;
